import React from 'react';
import ProductPageTemplate from '../../components/ProductPageTemplate';

const DATA = {
  hero: {
    label: 'Resume Intelligence — Recruiter Exclusive',
    title: 'See beyond the resume. Detect depth, ownership, and authenticity.',
    subtitle: 'AI-powered forensic analysis that flags shallow resumes, detects genuine ownership signals, and generates targeted follow-up questions — so you never waste time on inflated candidates.',
    gradient: 'linear-gradient(150deg, #0076A3 0%, #44337A 50%, #5B21B6 100%)',
  },
  sections: [
    {
      label: 'Recruiter-Only Feature',
      title: 'Three scores that tell you everything',
      features: [
        { icon: '\ud83d\udcca', title: 'Depth Score (0-100)', desc: 'Measures how deeply the candidate demonstrates real expertise. Surface-level buzzwords score low; specific technologies, metrics, and trade-offs score high.' },
        { icon: '\ud83c\udfc6', title: 'Ownership Score (0-100)', desc: 'Detects whether the candidate led or just participated. Analyzes language patterns: "I architected" vs "contributed to", specific deliverables vs vague team claims.' },
        { icon: '\ud83d\udee1\ufe0f', title: 'Authenticity Score (0-100)', desc: 'Flags AI-generated resumes, copy-paste templates, and inflated claims. Cross-references skills listed vs evidence provided in experience section.' },
      ],
      columns: 3,
    },
    {
      label: 'What It Detects',
      title: 'Shallow flags that waste your interview time',
      desc: 'Resume Intelligence scans for specific red flags that indicate a candidate may not have the depth they claim. Each flag includes the exact evidence from the resume text.',
      bullets: [
        'No measurable outcomes or impact (high severity)',
        'Generic, role-template phrasing — boilerplate language',
        'Skills listed without evidence of actual use',
        'Unspecified scope and complexity of work',
        'AI-generated language patterns (ChatGPT-style resumes)',
        'Email mismatch between resume and application',
        'Employment gaps or timeline inconsistencies',
      ],
      visual: '\u26a0\ufe0f',
      visualLabel: 'Shallow Resume Flags',
    },
    {
      label: 'Ownership Signals',
      title: 'Know who actually did the work',
      desc: 'The system identifies positive ownership indicators — evidence that the candidate personally drove results, not just participated.',
      bullets: [
        '"I led / I architected / I designed" — first-person ownership language',
        'Specific metrics with before/after numbers',
        'Named technologies with context of how they were used',
        'Cross-functional leadership evidence',
        'Problem → Solution → Result narrative structure',
        'Each signal rated: Weak / Moderate / Strong',
      ],
      visual: '\ud83c\udfc5',
      visualLabel: 'Ownership Evidence Detection',
    },
    {
      label: 'AI-Generated Follow-Ups',
      title: 'Never go into an interview unprepared',
      desc: 'Based on resume gaps and ownership uncertainties, AI generates 5-7 targeted follow-up questions designed to probe the exact areas where the resume is weakest.',
      bullets: [
        'Each question targets a specific skill gap or unverified claim',
        'Probing depth levels: Surface Check → Ownership Verification → Technical Deep-Dive',
        'Questions adapt to the job description requirements',
        'Copy directly into your interview prep',
        'Exposes rehearsed answers by asking for specifics the resume lacks',
      ],
      visual: '\u2753',
      visualLabel: 'AI Follow-Up Question Generator',
    },
    {
      label: 'How Recruiters Use It',
      title: 'Integrated into your candidate workflow',
      features: [
        { icon: '\ud83d\udc41\ufe0f', title: 'One-Click Analysis', desc: 'Click "Analyze Depth" on any candidate profile. AI scans the resume and returns a full intelligence report in seconds.' },
        { icon: '\ud83d\udcdd', title: 'Persistent Results', desc: 'Analysis is cached and displayed in the "Resume Intelligence" tab. No need to re-run — results persist until you re-analyze.' },
        { icon: '\ud83d\udd12', title: 'Recruiter-Only Access', desc: 'Candidates never see their depth analysis. This is an internal recruiter tool for making informed screening decisions.' },
        { icon: '\ud83c\udfaf', title: 'Pre-Interview Prep', desc: 'Use the AI-generated follow-up questions to create a targeted interview that probes exactly where the resume is weakest.' },
      ],
      columns: 4,
    },
  ],
  testimonial: {
    quote: 'Resume Intelligence saved us from hiring a candidate who looked great on paper but could not answer basic questions about their own listed achievements. The depth score was 22 — we would have missed that without this tool.',
    name: 'David L.',
    role: 'Head of Engineering, ScaleUp Inc.',
  },
  cta: {
    title: 'Stop guessing. Start knowing.',
    desc: 'Analyze any resume in seconds. Available exclusively for recruiters on Growth and Enterprise plans.',
  },
};

export default function ResumeIntelligencePage() { return <ProductPageTemplate data={DATA} />; }
